import type { KnownMapping, MappingSelectionRegion, ParsedWorksheet } from "@/types";

const PROMOTED_REGION_PREFIX = "user_promoted_";

/** Worksheets auto-detected as mapping candidates (before user selection). */
export function getMappingWorksheets(worksheets: ParsedWorksheet[]): ParsedWorksheet[] {
  return worksheets.filter(
    (ws) =>
      !ws.isMetadata && ws.previewCategory !== "non_mapping" && ws.previewCategory !== "ambiguity"
  );
}

export function buildPreviewKey(
  category: NonNullable<ParsedWorksheet["previewCategory"]>,
  id: string,
  sectionIndex?: number
): string {
  if (category === "mapping") {
    return `mapping:${id}`;
  }
  const index = sectionIndex ?? 0;
  return `${category}:${id}:${index}`;
}

export function userPromotedRegionId(sourceRegionId: string, sectionIndex: number): string {
  return `${PROMOTED_REGION_PREFIX}${sourceRegionId}_${sectionIndex}`;
}

export function isPromotedRegionId(regionId: string): boolean {
  return regionId.startsWith(PROMOTED_REGION_PREFIX);
}

/** Region id used by the mapping pipeline for a worksheet (native or user-promoted). */
export function resolveMappingRegionId(ws: ParsedWorksheet): string {
  if (ws.regionId) {
    return ws.regionId;
  }
  if (ws.sourceRegionId != null && ws.sectionIndex != null) {
    return userPromotedRegionId(ws.sourceRegionId, ws.sectionIndex);
  }
  return ws.previewKey ?? ws.name;
}

/** Trailing digits from a region id (`region_group_002` → 2). */
export function regionIdNumericKey(regionId: string | null | undefined): number | null {
  const trimmed = regionId?.trim() ?? "";
  if (!trimmed) return null;
  const match = trimmed.match(/(\d+)$/);
  if (!match) return null;
  return Number.parseInt(match[1]!, 10);
}

/** Exact id match, or the same trailing numeric group (`region_group_002` vs `002`). */
export function regionIdsMatch(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  const left = a?.trim() ?? "";
  const right = b?.trim() ?? "";
  if (!left || !right) return false;
  if (left === right) return true;
  const leftKey = regionIdNumericKey(left);
  const rightKey = regionIdNumericKey(right);
  return leftKey != null && rightKey != null && leftKey === rightKey;
}

/**
 * Sort key for region ids: numbered groups ascending, then unnumbered by localeCompare.
 * Prefer this over alphabetical sheet/name order for multi-table mapping.
 */
export function compareRegionIdsByNumber(
  a: string | null | undefined,
  b: string | null | undefined
): number {
  const aKey = regionIdNumericKey(a);
  const bKey = regionIdNumericKey(b);
  if (aKey != null && bKey != null && aKey !== bKey) {
    return aKey - bKey;
  }
  if (aKey != null && bKey == null) return -1;
  if (aKey == null && bKey != null) return 1;
  return (a?.trim() ?? "").localeCompare(b?.trim() ?? "", undefined, { sensitivity: "base" });
}

/** Stable sort of worksheets by mapping region id number. */
export function sortWorksheetsByRegionNumber(worksheets: ParsedWorksheet[]): ParsedWorksheet[] {
  return [...worksheets].sort((left, right) =>
    compareRegionIdsByNumber(resolveMappingRegionId(left), resolveMappingRegionId(right))
  );
}

export interface ExtractionMappingTarget {
  regionId: string;
  sourceColumns: string[];
}

/**
 * Resolve the extraction region + headers for complex-ingestion mapping.
 * Prefer these over re-parsing the Excel via /process so source_field names
 * match extraction mapped_data keys (including single-region files).
 */
export function resolveExtractionMappingTarget(params: {
  isMultiRegion: boolean;
  activeRegionId?: string | null;
  activeRegionHeaders?: string[] | null;
  worksheets: ParsedWorksheet[];
  mappingSelection: Record<string, boolean>;
  inputColumns: string[];
}): ExtractionMappingTarget | null {
  if (params.isMultiRegion) {
    const regionId = (params.activeRegionId ?? "").trim();
    if (!regionId) return null;
    const headers =
      params.activeRegionHeaders && params.activeRegionHeaders.length > 0
        ? params.activeRegionHeaders
        : params.inputColumns;
    return { regionId, sourceColumns: headers };
  }

  const selected = getSelectedMappingWorksheets(params.worksheets, params.mappingSelection);
  const ws = selected[0] ?? getMappingWorksheets(params.worksheets)[0];
  if (!ws) return null;
  const regionId = resolveMappingRegionId(ws).trim();
  if (!regionId) return null;
  const headers = ws.headers.length > 0 ? ws.headers : params.inputColumns;
  return { regionId, sourceColumns: headers };
}

export function getWorksheetKey(ws: ParsedWorksheet, index: number): string {
  return ws.previewKey ?? ws.regionId ?? ws.name ?? `region_${index + 1}`;
}

/** @deprecated Prefer getWorksheetKey — kept for report-type maps keyed by region id. */
export function getRegionKey(ws: ParsedWorksheet, index: number): string {
  return getWorksheetKey(ws, index);
}

export function isDefaultIncludedWorksheet(ws: ParsedWorksheet): boolean {
  return ws.previewCategory === "mapping" || (!ws.previewCategory && !ws.isMetadata);
}

export function buildDefaultMappingSelection(
  worksheets: ParsedWorksheet[]
): Record<string, boolean> {
  return Object.fromEntries(
    worksheets.map((ws, index) => [getWorksheetKey(ws, index), isDefaultIncludedWorksheet(ws)])
  );
}

/** Initial confirm-structure state: every region included until the user opts out. */
export function buildAllSelectedMappingSelection(
  worksheets: ParsedWorksheet[]
): Record<string, boolean> {
  return Object.fromEntries(worksheets.map((ws, index) => [getWorksheetKey(ws, index), true]));
}

/** Restore checkbox state from a previously saved mapping-selection payload. */
export function buildMappingSelectionFromSaved(
  worksheets: ParsedWorksheet[],
  savedRegions: MappingSelectionRegion[] | undefined | null,
  fallbackToDefault = true
): Record<string, boolean> {
  if (!savedRegions?.length) {
    return fallbackToDefault ? buildDefaultMappingSelection(worksheets) : {};
  }
  const savedByKey = new Map(savedRegions.map((region) => [region.previewKey, region.included]));
  return Object.fromEntries(
    worksheets.map((ws, index) => {
      const key = getWorksheetKey(ws, index);
      const saved = savedByKey.get(key);
      return [key, saved ?? fallbackToDefault];
    })
  );
}

/** Ensure every worksheet has preview metadata for the confirm-structure panel. */
export function normalizeWorksheetPreviewMetadata(
  worksheets: ParsedWorksheet[]
): ParsedWorksheet[] {
  return worksheets.map((ws, index) => {
    if (ws.previewCategory && ws.previewKey) {
      return ws;
    }
    const inferredCategory: ParsedWorksheet["previewCategory"] =
      ws.previewCategory ??
      (ws.name.startsWith("Non-Mapping")
        ? "non_mapping"
        : ws.name.startsWith("Ambiguity")
          ? "ambiguity"
          : ws.isMetadata
            ? "non_mapping"
            : "mapping");
    const previewKey =
      ws.previewKey ??
      (inferredCategory === "mapping"
        ? buildPreviewKey("mapping", ws.regionId ?? ws.name)
        : buildPreviewKey(
            inferredCategory,
            ws.sourceRegionId ?? ws.regionId ?? ws.name,
            ws.sectionIndex ?? index
          ));
    return {
      ...ws,
      previewCategory: inferredCategory,
      previewKey,
    };
  });
}

export function isWorksheetSelected(
  ws: ParsedWorksheet,
  index: number,
  selection: Record<string, boolean>
): boolean {
  return selection[getWorksheetKey(ws, index)] === true;
}

/** Worksheets the user chose to include in mapping on the confirm-structure step. */
export function getSelectedMappingWorksheets(
  worksheets: ParsedWorksheet[],
  selection: Record<string, boolean>
): ParsedWorksheet[] {
  return worksheets.filter((ws, index) => isWorksheetSelected(ws, index, selection));
}

export function countSelectedMappingWorksheets(
  worksheets: ParsedWorksheet[],
  selection: Record<string, boolean>
): number {
  return getSelectedMappingWorksheets(worksheets, selection).length;
}

/** Total source rows shown on pipeline cards (selected regions when provided). */
export function countPipelineSourceRows(
  worksheets: ParsedWorksheet[],
  selection?: Record<string, boolean>
): number {
  const included =
    selection && Object.keys(selection).length > 0
      ? getSelectedMappingWorksheets(worksheets, selection)
      : worksheets.filter((ws) => !ws.isMetadata);
  return included.reduce((sum, ws) => sum + ws.totalRows, 0);
}

/** Selected table count for pipeline cards (mirrors row-count selection rules). */
export function countPipelineTables(
  worksheets: ParsedWorksheet[],
  selection?: Record<string, boolean>
): number {
  if (selection && Object.keys(selection).length > 0) {
    return countSelectedMappingWorksheets(worksheets, selection);
  }
  return worksheets.filter((ws) => !ws.isMetadata).length;
}

/** Per-region source row totals for multi-table DQ pass/fail denominators. */
export function buildRegionRowCounts(
  worksheets: ParsedWorksheet[],
  selection?: Record<string, boolean>
): Record<string, number> {
  const included =
    selection && Object.keys(selection).length > 0
      ? getSelectedMappingWorksheets(worksheets, selection)
      : worksheets.filter((ws) => !ws.isMetadata);
  const counts: Record<string, number> = {};
  for (const ws of included) {
    const regionId = resolveMappingRegionId(ws).trim();
    if (!regionId) continue;
    counts[regionId] = (counts[regionId] ?? 0) + (ws.totalRows || 0);
  }
  return counts;
}

/** Worksheet for a mapping region id, or null when not present in the preview. */
export function selectWorksheetForRegion(
  worksheets: ParsedWorksheet[],
  regionId: string | null | undefined,
  options?: { tableIndex?: number }
): ParsedWorksheet | null {
  const target = regionId?.trim();
  if (target) {
    const exact = worksheets.find((ws) => resolveMappingRegionId(ws).trim() === target);
    if (exact) return exact;

    const targetKey = regionIdNumericKey(target);
    if (targetKey != null) {
      const numeric = worksheets.find(
        (ws) => regionIdNumericKey(resolveMappingRegionId(ws)) === targetKey
      );
      if (numeric) return numeric;
    }
  }

  const tableIndex = options?.tableIndex;
  if (tableIndex != null && tableIndex >= 0 && worksheets.length > 0) {
    const ordered = sortWorksheetsByRegionNumber(worksheets);
    return ordered[tableIndex] ?? null;
  }

  return null;
}

/** Mappings that belong to a region (or unscoped mappings when regionId is empty). */
export function filterMappingsForRegion(
  mappings: KnownMapping[],
  regionId: string | null | undefined
): KnownMapping[] {
  const target = regionId?.trim();
  if (!target) return mappings;
  return mappings.filter((m) => {
    const mid = m.regionId?.trim();
    return !mid || regionIdsMatch(mid, target);
  });
}

export function buildEmptyRegionReportTypes(
  worksheets: ParsedWorksheet[],
  selection?: Record<string, boolean>
): Record<string, string | null> {
  const selected = selection
    ? getSelectedMappingWorksheets(worksheets, selection)
    : getMappingWorksheets(worksheets);
  return Object.fromEntries(selected.map((ws, index) => [getWorksheetKey(ws, index), null]));
}

/** Prefill every selected region with a file-level report type from upload. */
export function buildSeededRegionReportTypes(
  worksheets: ParsedWorksheet[],
  selection: Record<string, boolean> | undefined,
  seedReportType: string | null | undefined
): Record<string, string | null> {
  const empty = buildEmptyRegionReportTypes(worksheets, selection);
  const seed = seedReportType?.trim();
  if (!seed || seed === "unknown") return empty;
  return Object.fromEntries(Object.keys(empty).map((key) => [key, seed]));
}

export function allRegionReportTypesSet(
  worksheets: ParsedWorksheet[],
  regionReportTypes: Record<string, string | null>,
  selection?: Record<string, boolean>
): boolean {
  const selected = selection
    ? getSelectedMappingWorksheets(worksheets, selection)
    : getMappingWorksheets(worksheets);
  return selected.every((ws, index) => {
    const value = regionReportTypes[getWorksheetKey(ws, index)];
    return !!value && value !== "unknown";
  });
}

export function buildMappingSelectionPayload(
  worksheets: ParsedWorksheet[],
  selection: Record<string, boolean>
): MappingSelectionRegion[] {
  return worksheets.map((ws, index) => {
    const previewKey = getWorksheetKey(ws, index);
    const included = selection[previewKey] === true;
    const promoted =
      included && (ws.previewCategory === "non_mapping" || ws.previewCategory === "ambiguity");
    return {
      previewKey,
      included,
      promoted,
      regionId: ws.regionId ?? null,
      sourceRegionId: ws.sourceRegionId ?? ws.sourceRegionIds?.[0] ?? null,
      sectionIndex: ws.sectionIndex ?? null,
      previewCategory: ws.previewCategory,
    };
  });
}

/** i18n parts for Confirm Structure–style table labels in Mapping. */
export interface MappingRegionTableLabelParts {
  n: number;
  sheets?: string;
  [key: string]: string | number | undefined;
}

/**
 * Label parts for a mapping region at a 1-based ordinal in the Mapping list.
 * Prefer sheet names from Confirm Structure; never use region_group_* as the label.
 */
export function mappingRegionTableLabelParts(
  region: { sheetNames?: string[] | null } | null | undefined,
  ordinal: number
): MappingRegionTableLabelParts {
  const sheets =
    region?.sheetNames
      ?.map((s) => s.trim())
      .filter(Boolean)
      .join(", ") ?? "";
  if (sheets) {
    return { n: ordinal, sheets };
  }
  return { n: ordinal };
}

/** i18n key matching Confirm Structure table labels. */
export function mappingRegionTableLabelKey(
  parts: MappingRegionTableLabelParts
): "upload.tableOrdinalWithSheetsLabel" | "upload.tableOrdinalLabel" {
  return parts.sheets ? "upload.tableOrdinalWithSheetsLabel" : "upload.tableOrdinalLabel";
}

/** English Confirm Structure label: `Table N` or `Table N - sheets`. */
export function formatTableOrdinalLabel(ordinal: number, sheets?: string | null): string {
  const parts = mappingRegionTableLabelParts(
    sheets?.trim() ? { sheetNames: [sheets.trim()] } : undefined,
    ordinal
  );
  return parts.sheets ? `Table ${parts.n} - ${parts.sheets}` : `Table ${parts.n}`;
}
